/**
 * spm埋点通用方法，包含PV、手动曝光、点击埋点
 * PC和M通用，只在PV有区别，PV接收参数里有inApp，cb函数为promise，在app内获取上一跳等信息用
 * json_param、event_param， 需要 JSON.stringify
 */

// https://joyspace.jd.com/h/personal/pages/Rs5VGHKO7SHsC8HCnRFo

import { cookieUtil } from '@/intl-jdi-common-ssr/utils/cookieUtils';
import {
  appendClickHistory,
  fetchClickHistory,
  fetchSessionPageHistory,
} from '@/intl-jdi-common-ssr/utils/spmTrackUtils/trackHistory';

export interface PVParam {
  spm: string;
  page_id: string;
  page_param?: object;
  inApp?: boolean;
  getAppMtaParams?: () => Promise<any>;
}

export interface CLParam {
  spm: string;
  biz_type?: string;
  event_id?: string;
  page_id?: string;
  json_param?: string;
  event_param?: string;
  target?: HTMLElement; // e.target
}

export interface ExpParam {
  target?: HTMLElement;
  spm: string;
  biz_type?: string;
  event_id?: string;
  page_id?: string;
  json_param?: string;
  event_param?: string;
}

export const spmTrackingUtils = {
  // pageId = ''
  // -----------浏览上报-------------
  async spmPV({ spm, page_id, page_param, inApp = false, getAppMtaParams }: PVParam) {
    try {
      // 获取之前的记录
      const history: string[] = fetchSessionPageHistory();
      // 获取页面的referrer
      const referrer = document.referrer;
      // 判断上一个页面是否为站内页面，如果为站外页面并且有referrer则上传referrer，并且删除sessionStorage，否则上传page_id
      const outerUrl = referrer && new URL(referrer).host !== location.host;

      // 上一个
      let refPage = outerUrl ? referrer : history[0] || '-100';
      // 上上一个
      let preRef = history[1] || '-100';

      // 每次发送pv前，更新history
      // 判断是否在app内，如果在app内则获取ref、preRef 相关参数，设置
      if (inApp) {
        // 如果在app内，需要等待mta获取app中的历史记录，并且插入history中
        await getAppMtaParams?.()
          .then((payload: any) => {
            console.log('getMtaParams', payload);
            if (payload.code === 0 || payload.code === '0') {
              refPage = payload?.data?.refPage || refPage;
              preRef = payload?.data?.preRefPage || preRef;

              // click history
              if (payload.data.preRefClickEvent) {
                const clickData = {
                  curClickEvent: payload.data.preRefClickEvent,
                  curPageId: payload.data.preRefPage,
                  curClickParam: payload.data.preRefClickParam,
                  // app bridge传递的spm
                  curClickSpm: payload?.data?.pre_spm ?? '',
                };
                appendClickHistory(clickData);
              }

              if (payload.data.refClickEvent) {
                const clickData = {
                  curClickEvent: payload.data.refClickEvent,
                  curPageId: payload.data.refPage,
                  curClickParam: payload.data.refClickParam,
                  // app bridge传递的spm
                  curClickSpm: payload?.data?.ref_spm ?? '',
                };
                appendClickHistory(clickData);
              }
            }

            // app中，修改history
            history[0] = refPage;
            history[1] = preRef;
          })
          .catch((error: any) => {
            // logger.error({
            //   eventName: 'app_mta_params_get_error',
            //   mesage: '获取app mta参数失败',
            //   error,
            // });
          });
      }

      // spm参数代替page_id
      if (spm !== history[0]) {
        // 将新记录插入到数组开头
        history.unshift(spm);
        // 限制数组长度为 5
        while (history.length > 5) {
          history.pop(); // 移除最后的记录
        }
      }

      // 存储更新后的历史记录
      cookieUtil.setSessionStorage('pageHistory', JSON.stringify(history));

      const clickHistory = fetchClickHistory();

      // from_page: 上一个页面id. SDK自动收集且为URL而不是pageid，前端无法修改，因此增加字段 refPage 用于给数据那边获取上一跳的pageid
      const paramObj = { ...(page_param ?? {}), refPage, preRef };

      // 点击相关的数据，放到pv层级
      const clickParam: any = {};
      if (clickHistory[0]) {
        clickParam.refclick_event = clickHistory[0].curClickEvent;
        clickParam.refclick_param = JSON.stringify(clickHistory[0].curClickParam) || '-100';
        clickParam.ref_spm = clickHistory[0].curClickSpm || '-100';
        if (clickHistory[0].curClickParam?.page_id?.length > 0) {
          clickParam.ref = clickHistory[0].curClickParam?.page_id || '-100';
        }
        if (clickHistory[1]) {
          clickParam.prerefclick_event = clickHistory[1].curClickEvent;
          clickParam.prerefclick_param = JSON.stringify(clickHistory[1].curClickParam) || '-100';
          clickParam.pre_spm = clickHistory[1].curClickSpm || '-100';
        }
      }

      const spmPV = new window.MPing.inputs.SpmPV({
        spm,
        page_id: page_id || '',
        page_param: JSON.stringify(paramObj),
        ...clickParam,
      });

      new window.MPing().send(spmPV);
    } catch (e) {}
  },

  //------------点击上报---------------
  spmClick({
    spm,
    biz_type,
    event_id = '',
    page_id = '',
    json_param,
    event_param,
    target,
  }: CLParam) {
    try {
      const click = new window.MPing.inputs.SpmClick({
        spm,
        biz_type,
        event_id,
        page_id,
        json_param,
        event_param,
        target,
      });

      // 保存点击事件信息到session
      const history: string[] = fetchSessionPageHistory();
      const clickData = {
        // 添加spm值for上一页点击
        curClickSpm: spm,
        curClickEvent: event_id || undefined,
        curPageId: page_id || history[0] || '',
        curClickParam: json_param,
      };
      appendClickHistory(clickData); // 将点击事件记录到历史中

      new window.MPing().send(click);
    } catch (e) {}
  },

  spmExpo({
    target,
    spm,
    biz_type,
    event_id = '',
    page_id = '',
    json_param,
    event_param,
  }: ExpParam) {
    try {
      const exposureParams: ExpParam = {
        spm,
        biz_type,
        event_id,
        page_id,
        json_param,
        event_param,
      };
      // 只有当 target 存在时才添加到参数中
      if (target) {
        exposureParams.target = target;
      }
      const exposure = new window.MPing.inputs.SpmExposure(exposureParams);
      new window.MPing().send(exposure);
    } catch (e) {
      console.log('SPM_ERROR: ', e);
    }
  },
};
